import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ManifestDetailSingleInterface,
  ManifestHeaderInterface,
} from "../../interfaces/ManifestInterface";
import { DispatchManifestFormValues } from "../../modules/Manifest/DispatchManifestForm";

interface DispatchState {
  cleaning: boolean;
  loadFromDB: boolean;
  dispatchForm: DispatchManifestFormValues;
  dispatchHeader: ManifestHeaderInterface | null;
  pendingDetails: { [id: string]: ManifestDetailSingleInterface };
  processedDetails: { [id: string]: ManifestDetailSingleInterface };
}

const initialState: DispatchState = {
  cleaning: false,
  loadFromDB: false,
  dispatchForm: {
    route: null,
    consignee: null,
    vehicle: null,
    driver: null,
    seal1: "",
    seal2: "",
    seal3: "",
    observation: "",
  },
  dispatchHeader: null,
  pendingDetails: {},
  processedDetails: {},
};

const dispatch = createSlice({
  name: "dispatch",
  initialState,
  reducers: {
    setDispatchForm: (
      state,
      action: PayloadAction<DispatchManifestFormValues>
    ) => {
      state.dispatchForm = action.payload;
    },
    setDispatchHeader: (
      state,
      action: PayloadAction<ManifestHeaderInterface | null>
    ) => {
      state.dispatchHeader = action.payload;
    },
    setDispatchCleaning: (state, action: PayloadAction<boolean>) => {
      state.cleaning = action.payload;
    },
    setDispatchLoadFromDB: (state, action: PayloadAction<boolean>) => {
      state.loadFromDB = action.payload;
    },
    clearDispatchValues: (state) => {
      state.dispatchForm = {
        ...state.dispatchForm,
        vehicle: null,
        driver: null,
        seal1: "",
        seal2: "",
        seal3: "",
        observation: "",
      };
      state.dispatchHeader = null;
      state.pendingDetails = {};
      state.processedDetails = {};
      state.cleaning = true;
    },
    addPendingDetails: (
      state,
      action: PayloadAction<ManifestDetailSingleInterface[]>
    ) => {
      for (const detail of action.payload) {
        const id = `${detail.shipmentNumber}-${detail.pieceNumber}`;
        if (!(id in state.pendingDetails)) {
          state.pendingDetails[id] = {
            ...detail,
            _date: new Date().toISOString(),
          };
        }
      }
    },
    createPendingDetails: (
      state,
      action: PayloadAction<ManifestDetailSingleInterface[]>
    ) => {
      const pendingDetails: { [id: string]: ManifestDetailSingleInterface } =
        {};
      for (const detail of action.payload) {
        const id = `${detail.shipmentNumber}-${detail.pieceNumber}`;
        pendingDetails[id] = {
          ...detail,
          _date: new Date().toISOString(),
        };
      }
      state.pendingDetails = pendingDetails;
    },
    updatePendingDetails: (
      state,
      action: PayloadAction<ManifestDetailSingleInterface[]>
    ) => {
      for (const detail of action.payload) {
        const id = `${detail.shipmentNumber}-${detail.pieceNumber}`;
        if (id in state.pendingDetails) {
          state.pendingDetails[id] = {
            ...detail,
            _date: new Date().toISOString(),
          };
        }
      }
    },
    removePendingDetails: (
      state,
      action: PayloadAction<ManifestDetailSingleInterface[]>
    ) => {
      const pendingDetails: { [id: string]: ManifestDetailSingleInterface } =
        {};
      const idsToRemove = new Set(
        action.payload.map((d) => `${d.shipmentNumber}-${d.pieceNumber}`)
      );

      for (const id in state.pendingDetails) {
        if (!idsToRemove.has(id)) {
          pendingDetails[id] = state.pendingDetails[id];
        }
      }

      state.pendingDetails = pendingDetails;
    },
    addProcessedDetails: (
      state,
      action: PayloadAction<ManifestDetailSingleInterface[]>
    ) => {
      for (const detail of action.payload) {
        const id = `${detail.shipmentNumber}-${detail.pieceNumber}`;
        if (!(id in state.processedDetails)) {
          state.processedDetails[id] = {
            ...detail,
            _date: new Date().toISOString(),
          };
        }
      }
    },
    updateProcessedDetails: (
      state,
      action: PayloadAction<ManifestDetailSingleInterface[]>
    ) => {
      for (const detail of action.payload) {
        const id = `${detail.shipmentNumber}-${detail.pieceNumber}`;
        if (id in state.processedDetails) {
          state.processedDetails[id] = {
            ...detail,
            _date: new Date().toISOString(),
          };
        }
      }
    },
    removeProcessedDetails: (
      state,
      action: PayloadAction<ManifestDetailSingleInterface[]>
    ) => {
      const processedDetails: { [id: string]: ManifestDetailSingleInterface } =
        {};
      const idsToRemove = new Set(
        action.payload.map((d) => `${d.shipmentNumber}-${d.pieceNumber}`)
      );

      for (const id in state.processedDetails) {
        if (!idsToRemove.has(id)) {
          processedDetails[id] = state.processedDetails[id];
        }
      }

      state.processedDetails = processedDetails;
    },
    removeAllProcessedDetails: (state) => {
      state.processedDetails = {};
    },
  },
});

export const {
  setDispatchForm,
  setDispatchHeader,
  setDispatchCleaning,
  setDispatchLoadFromDB,
  clearDispatchValues,
  addPendingDetails,
  createPendingDetails,
  updatePendingDetails,
  removePendingDetails,
  addProcessedDetails,
  updateProcessedDetails,
  removeProcessedDetails,
  removeAllProcessedDetails,
} = dispatch.actions;

export default dispatch.reducer;
